import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ComputerDesktopIcon,
  CameraIcon,
  ArrowRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

export const CompactProductCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const products = [
    {
      id: 1,
      name: "DICOMFiles",
      description: "Automatización completa del flujo DICOM. Reduce trabajo manual en 80-90%.",
      icon: <ComputerDesktopIcon className="h-12 w-12" />,
      features: ["Procesamiento 24/7", "Integración PACS", "Automatización PDFs"],
      price: "Desde $299/mes",
      link: "/dicomfiles",
      cta: "Ver Detalles",
      gradient: "from-blue-500 to-blue-700"
    },
    {
      id: 2,
      name: "DICOMCapture",
      description: "Captura y procesamiento de imágenes DICOM desde equipos médicos.",
      icon: <CameraIcon className="h-12 w-12" />,
      features: ["Captura automática", "Validación calidad", "Tiempo real"],
      price: "Desde $599/mes",
      link: "/contact",
      cta: "Más Información",
      gradient: "from-green-500 to-green-700"
    }
  ];

  // Auto-advance slides every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [products.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % products.length);
  };

  return (
    <div className="relative w-full h-[400px] overflow-hidden rounded-xl shadow-lg">
      {/* Slides */}
      <div 
        className="flex transition-transform duration-500 ease-in-out h-full"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {products.map((product) => (
          <div
            key={product.id}
            className={`w-full h-full flex-shrink-0 bg-gradient-to-br ${product.gradient} relative`}
          >
            <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-6xl mx-auto px-6 w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  {/* Content */}
                  <div className="text-white">
                    <div className="mb-6">
                      <div className="text-white mb-4 flex items-center">
                        {product.icon}
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold mb-3">
                        {product.name}
                      </h3>
                      <p className="text-lg text-white/90 mb-4">
                        {product.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <ul className="space-y-2">
                        {product.features.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <CheckIcon className="h-4 w-4 text-white mr-2 flex-shrink-0" />
                            <span className="text-white/90 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Price and CTA */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="text-xl font-bold text-white">
                        {product.price}
                      </div>
                      <Link
                        to={product.link}
                        className="bg-white text-gray-900 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 flex items-center text-sm"
                      >
                        {product.cta}
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                      </Link>
                    </div>
                  </div>

                  {/* Visual Element */}
                  <div className="hidden md:block">
                    <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                      <div className="text-center text-white">
                        <div className="mb-4 flex justify-center">
                          <div className="h-16 w-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            {product.icon}
                          </div>
                        </div>
                        <h4 className="text-xl font-bold mb-2">{product.name}</h4>
                        <div className="text-white/80 text-sm">
                          {product.description}
                        </div>
                        <div className="mt-4 text-lg font-bold">
                          {product.price}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all duration-200 z-20"
        aria-label="Slide anterior"
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all duration-200 z-20"
        aria-label="Siguiente slide"
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentSlide 
                ? 'bg-white' 
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            }`}
            aria-label={`Ir al slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
